import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import ScrollToTop from "../components/ScrollToTop";
import { ToastContainer } from "react-toastify";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import CampaignBanner from "../features/campaign/CampaignBanner.js";
import AwardCelebrations from "../features/profile/AwardCelebrations.js";

export default function RootLayout() {
  const theme = useTheme();
  const isLessThan480 = useMediaQuery("(max-width:480px)");

  // Publishes how far the on-screen keyboard reaches up from the bottom of the layout
  // viewport as --keyboard-inset, so mobile toasts can sit just above it. Done per
  // element rather than via interactive-widget=resizes-content, which would push every
  // fixed element (e.g. the FAB) up with the keyboard too. Neither iOS nor Android
  // shrinks the layout viewport by default, so innerHeight stays the full screen while
  // the visual viewport shrinks (and on iOS can scroll, hence offsetTop).
  //
  // Android Chrome also draws its autofill accessory bar (key/card/location icons)
  // above the keyboard without counting it in the visual viewport, so its height is
  // added on while the keyboard is open (anything over 150px can only be a keyboard).
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const isAndroid = /Android/i.test(navigator.userAgent);
    const ANDROID_ACCESSORY_BAR_PX = 16;
    const update = () => {
      const keyboardInset = Math.max(
        0,
        window.innerHeight - vv.height - vv.offsetTop,
      );
      const inset =
        isAndroid && keyboardInset > 150
          ? keyboardInset + ANDROID_ACCESSORY_BAR_PX
          : keyboardInset;
      document.documentElement.style.setProperty(
        "--keyboard-inset",
        `${Math.round(inset)}px`,
      );
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  return (
    <>
      <ScrollToTop />
      {/*
        Covers the safe-area top inset — the band the system status bar sits over once
        the page is laid out edge-to-edge — with the app background, so scrolling
        content doesn't slide around behind the clock and battery icons.

        Looks like dead code, and currently is: measured Sept 2026, --safe-top is 0px on
        both platforms (iOS lays the web view below the status bar under
        apple-mobile-web-app-status-bar-style "default"; Android doesn't honour
        viewport-fit=cover in an installed PWA at all — see index.html). It's kept
        deliberately so the app is already correct whenever either platform starts
        reporting a top inset, which on Android will arrive via a Chrome auto-update
        with no warning. Verified by overriding --safe-top in devtools: without this,
        page content is visible above the header while scrolling.

        A fixed element rather than padding the headers, because HideOnScroll slides
        them out of view entirely — whatever covers this band has to outlive that. It
        also covers the five separate header components and the routes that have no
        AppBar at all (Home, PrivacyPolicy) in one place.

        Above the AppBars (zIndex.appBar, 1100) so it survives HideOnScroll, but below
        the campaign banner and dialogs so it never covers real content.
      */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "var(--safe-top)",
          bgcolor: "background.default",
          zIndex: theme.zIndex.appBar + 1,
        }}
      />
      <CampaignBanner />
      {/* Clears the safe-area top inset for in-flow content, once for every route
      (Spa pages and non-Spa pages like Home/PrivacyPolicy alike) rather than
      per-page. 0px outside notched/installed-PWA contexts. */}
      <Box sx={{ paddingTop: "var(--safe-top)" }}>
        <Outlet />
      </Box>
      {/* Mobile toasts sit at the bottom (positioned in index.js's global styles) so
      they never cover the top-pinned search/dialog inputs; newest nearest the edge. */}
      {/* Unlock toasts for achievements earned anywhere in the app */}
      <AwardCelebrations />
      <ToastContainer
        position={isLessThan480 ? "bottom-center" : "top-right"}
        autoClose={2500}
        hideProgressBar
        newestOnTop={!isLessThan480}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={theme.palette.mode}
        style={
          isLessThan480 ? undefined : { marginTop: "var(--top-offset, 0px)" }
        }
      />
    </>
  );
}
